import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AudioService, AudioFile } from '../../core/services/audio';

interface UploadItem {
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
  result?: AudioFile;
}

@Component({
  selector: 'app-audio-library',
  imports: [CommonModule, FormsModule],
  templateUrl: './audio-library.html',
  styleUrl: './audio-library.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioLibraryComponent implements OnInit, OnDestroy {
  private audioService = inject(AudioService);

  // ─── List state ────────────────────────────────────────────────────────────
  audioFiles = signal<AudioFile[]>([]);
  total = signal(0);
  isLoading = signal(false);
  errorMsg = signal<string | null>(null);
  searchQuery = signal('');
  skip = signal(0);
  readonly limit = 50;

  hasMore = computed(() => this.skip() + this.limit < this.total());

  // ─── Upload state ──────────────────────────────────────────────────────────
  showUploadPanel = signal(false);
  uploadClosing = signal(false);
  uploadItems = signal<UploadItem[]>([]);
  private uploadCloseTimer: ReturnType<typeof setTimeout> | null = null;
  isDragOver = signal(false);
  isUploading = signal(false);

  uploadDoneCount = computed(() => this.uploadItems().filter(i => i.status === 'done').length);
  uploadErrorCount = computed(() => this.uploadItems().filter(i => i.status === 'error').length);
  uploadPendingCount = computed(() => this.uploadItems().filter(i => i.status === 'pending').length);

  // ─── Player state ──────────────────────────────────────────────────────────
  playingId = signal<string | null>(null);
  currentTime = signal(0);
  duration = signal(0);
  private audioEl: HTMLAudioElement | null = null;

  // ─── Edit modal ────────────────────────────────────────────────────────────
  editingFile = signal<AudioFile | null>(null);
  editTitle = signal('');
  editTags = signal('');
  isSavingEdit = signal(false);
  editError = signal<string | null>(null);

  // ─── Delete state ──────────────────────────────────────────────────────────
  fileToDelete = signal<AudioFile | null>(null);
  isDeleting = signal(false);

  // ─── Selected for batch delete ─────────────────────────────────────────────
  selectedIds = signal<Set<string>>(new Set());
  isBatchDeleting = signal(false);

  selectedCount = computed(() => this.selectedIds().size);

  ngOnInit(): void {
    this.loadAudio();
  }

  ngOnDestroy(): void {
    this.stopAudio();
    if (this.uploadCloseTimer) clearTimeout(this.uploadCloseTimer);
  }

  // ─── Load ───────────────────────────────────────────────────────────────────

  loadAudio(append = false): void {
    this.isLoading.set(true);
    this.errorMsg.set(null);
    this.audioService.list(this.skip(), this.limit, this.searchQuery() || undefined).subscribe({
      next: (res) => {
        this.audioFiles.set(append ? [...this.audioFiles(), ...res.items] : res.items);
        this.total.set(res.total);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.errorMsg.set(err?.error?.detail || 'Failed to load audio files.');
        this.isLoading.set(false);
      },
    });
  }

  onSearch(): void {
    this.skip.set(0);
    this.selectedIds.set(new Set());
    this.loadAudio();
  }

  loadMore(): void {
    this.skip.update(s => s + this.limit);
    this.loadAudio(true);
  }

  // ─── Upload ─────────────────────────────────────────────────────────────────

  openUploadPanel(): void {
    if (this.uploadCloseTimer) {
      clearTimeout(this.uploadCloseTimer);
      this.uploadCloseTimer = null;
    }
    this.uploadClosing.set(false);
    this.uploadItems.set([]);
    this.showUploadPanel.set(true);
  }

  closeUploadPanel(): void {
    if (this.isUploading() || !this.showUploadPanel() || this.uploadClosing()) return;
    this.uploadClosing.set(true);
    this.uploadCloseTimer = setTimeout(() => {
      this.showUploadPanel.set(false);
      this.uploadClosing.set(false);
      this.uploadItems.set([]);
      this.uploadCloseTimer = null;
    }, 200);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(): void {
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.addFiles(files);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    this.addFiles(files);
    input.value = '';
  }

  private addFiles(files: File[]): void {
    const audioTypes = ['audio/', 'video/webm', 'video/ogg'];
    const newItems: UploadItem[] = files
      .filter(f => audioTypes.some(t => f.type.startsWith(t)) || this.hasAudioExtension(f.name))
      .map(f => ({ file: f, status: 'pending' as const }));

    if (newItems.length === 0) return;
    this.uploadItems.update(items => [...items, ...newItems]);
  }

  private hasAudioExtension(name: string): boolean {
    return /\.(mp3|wav|ogg|flac|m4a|aac|wma|webm)$/i.test(name);
  }

  removeUploadItem(index: number): void {
    this.uploadItems.update(items => items.filter((_, i) => i !== index));
  }

  async startUpload(): Promise<void> {
    const items = this.uploadItems();
    const pending = items.filter(i => i.status === 'pending');
    if (!pending.length || this.isUploading()) return;

    this.isUploading.set(true);

    for (let i = 0; i < items.length; i++) {
      if (items[i].status !== 'pending') continue;

      // Mark as uploading
      this.uploadItems.update(list =>
        list.map((item, idx) => idx === i ? { ...item, status: 'uploading' } : item)
      );

      await new Promise<void>((resolve) => {
        this.audioService.uploadSingle(items[i].file).subscribe({
          next: (audio) => {
            this.uploadItems.update(list =>
              list.map((item, idx) => idx === i ? { ...item, status: 'done', result: audio } : item)
            );
            // Prepend to list
            this.audioFiles.update(list => [audio, ...list]);
            this.total.update(t => t + 1);
            resolve();
          },
          error: (err) => {
            const msg = err?.error?.detail || 'Upload failed.';
            this.uploadItems.update(list =>
              list.map((item, idx) => idx === i ? { ...item, status: 'error', error: msg } : item)
            );
            resolve();
          },
        });
      });
    }

    this.isUploading.set(false);
  }

  // ─── Audio player ───────────────────────────────────────────────────────────

  togglePlay(file: AudioFile): void {
    if (this.playingId() === file.id) {
      this.pauseAudio();
    } else {
      this.playAudio(file);
    }
  }

  private playAudio(file: AudioFile): void {
    this.stopAudio();

    this.audioEl = new Audio(file.file_url);
    this.audioEl.ontimeupdate = () => this.currentTime.set(this.audioEl?.currentTime ?? 0);
    this.audioEl.onloadedmetadata = () => this.duration.set(this.audioEl?.duration ?? 0);
    this.audioEl.onended = () => {
      this.playingId.set(null);
      this.currentTime.set(0);
    };
    this.audioEl.play();
    this.playingId.set(file.id);
    this.currentTime.set(0);
    this.duration.set(0);
  }

  private pauseAudio(): void {
    this.audioEl?.pause();
    this.playingId.set(null);
  }

  private stopAudio(): void {
    if (this.audioEl) {
      this.audioEl.pause();
      this.audioEl.src = '';
      this.audioEl = null;
    }
    this.playingId.set(null);
  }

  seek(event: Event): void {
    const input = event.target as HTMLInputElement;
    const time = parseFloat(input.value);
    if (this.audioEl) this.audioEl.currentTime = time;
    this.currentTime.set(time);
  }

  formatTime(seconds: number): string {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ─── Edit ───────────────────────────────────────────────────────────────────

  openEdit(file: AudioFile): void {
    this.editingFile.set(file);
    this.editTitle.set(file.title);
    this.editTags.set(file.tags.join(', '));
    this.editError.set(null);
    this.isSavingEdit.set(false);
  }

  closeEdit(): void {
    if (this.isSavingEdit()) return;
    this.editingFile.set(null);
  }

  saveEdit(): void {
    const file = this.editingFile();
    if (!file || this.isSavingEdit()) return;
    const title = this.editTitle().trim();
    if (!title) {
      this.editError.set('Title cannot be empty.');
      return;
    }

    const tags = this.editTags()
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    this.isSavingEdit.set(true);
    this.editError.set(null);
    this.audioService.update(file.id, { title, tags }).subscribe({
      next: (updated) => {
        this.audioFiles.update(list => list.map(f => f.id === updated.id ? updated : f));
        this.isSavingEdit.set(false);
        this.editingFile.set(null);
      },
      error: (err) => {
        this.editError.set(err?.error?.detail || 'Save failed.');
        this.isSavingEdit.set(false);
      },
    });
  }

  // ─── Delete ─────────────────────────────────────────────────────────────────

  openDelete(file: AudioFile): void {
    if (this.playingId() === file.id) this.stopAudio();
    this.fileToDelete.set(file);
  }

  closeDelete(): void {
    if (this.isDeleting()) return;
    this.fileToDelete.set(null);
  }

  confirmDelete(): void {
    const file = this.fileToDelete();
    if (!file || this.isDeleting()) return;
    this.isDeleting.set(true);
    this.audioService.delete(file.id).subscribe({
      next: () => {
        this.audioFiles.update(list => list.filter(f => f.id !== file.id));
        this.total.update(t => Math.max(0, t - 1));
        this.selectedIds.update(set => { const s = new Set(set); s.delete(file.id); return s; });
        this.isDeleting.set(false);
        this.fileToDelete.set(null);
      },
      error: (err) => {
        this.isDeleting.set(false);
        this.errorMsg.set(err?.error?.detail || 'Delete failed.');
        this.fileToDelete.set(null);
      },
    });
  }

  // ─── Batch select / delete ──────────────────────────────────────────────────

  toggleSelect(id: string): void {
    this.selectedIds.update(set => {
      const s = new Set(set);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  toggleSelectAll(): void {
    const all = this.audioFiles().map(f => f.id);
    if (this.selectedIds().size === all.length) {
      this.selectedIds.set(new Set());
    } else {
      this.selectedIds.set(new Set(all));
    }
  }

  allSelected(): boolean {
    const files = this.audioFiles();
    return files.length > 0 && this.selectedIds().size === files.length;
  }

  batchDelete(): void {
    const ids = Array.from(this.selectedIds());
    if (!ids.length || this.isBatchDeleting()) return;
    this.isBatchDeleting.set(true);
    // Stop player if playing one of the selected
    if (ids.includes(this.playingId() ?? '')) this.stopAudio();
    this.audioService.batchDelete(ids).subscribe({
      next: () => {
        this.audioFiles.update(list => list.filter(f => !ids.includes(f.id)));
        this.total.update(t => Math.max(0, t - ids.length));
        this.selectedIds.set(new Set());
        this.isBatchDeleting.set(false);
      },
      error: (err) => {
        this.errorMsg.set(err?.error?.detail || 'Batch delete failed.');
        this.isBatchDeleting.set(false);
      },
    });
  }

  mimeLabel(mime: string): string {
    const map: Record<string, string> = {
      'audio/mpeg': 'MP3',
      'audio/mp3': 'MP3',
      'audio/wav': 'WAV',
      'audio/x-wav': 'WAV',
      'audio/wave': 'WAV',
      'audio/ogg': 'OGG',
      'audio/flac': 'FLAC',
      'audio/x-flac': 'FLAC',
      'audio/x-m4a': 'M4A',
      'audio/mp4': 'M4A',
      'audio/aac': 'AAC',
      'audio/x-aac': 'AAC',
      'audio/x-ms-wma': 'WMA',
      'audio/wma': 'WMA',
      'audio/webm': 'WEBM',
    };
    return map[mime] ?? mime.split('/')[1]?.toUpperCase() ?? '—';
  }
}
